import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { tagsApi, tagsQueryKeys } from '../lib/tags-api';
import type {
	CreateTagRequest,
	MergeTagsRequest,
	Tag,
	TagSearchParams,
	TagSuggestParams,
	UpdateTagRequest,
} from '../types/tags';

/**
 * Hook for managing tags with React Query integration
 */
export function useTags() {
	const queryClient = useQueryClient();

	// Search tags
	const useTagSearch = (params: TagSearchParams, enabled = true) =>
		useQuery({
			queryKey: tagsQueryKeys.search(params.q || ''),
			queryFn: () => tagsApi.searchTags(params),
			enabled: enabled && (params.q?.length || 0) > 0,
			staleTime: 5 * 60 * 1000, // 5 minutes
		});

	// Get tag suggestions
	const useTagSuggestions = (params: TagSuggestParams = {}) =>
		useQuery({
			queryKey: tagsQueryKeys.suggest(params.limit),
			queryFn: () => tagsApi.suggestTags(params),
			staleTime: 10 * 60 * 1000, // 10 minutes
		});

	// Get paginated tags list
	const useTagsList = (page = 1, limit = 20) =>
		useQuery({
			queryKey: tagsQueryKeys.list(page, limit),
			queryFn: () => tagsApi.listTags(page, limit),
			staleTime: 5 * 60 * 1000,
		});

	// Get workflow tags
	const useWorkflowTags = (workflowId: string, enabled = true) =>
		useQuery({
			queryKey: tagsQueryKeys.workflow(workflowId),
			queryFn: () => tagsApi.getWorkflowTags(workflowId),
			enabled: enabled && !!workflowId,
			staleTime: 2 * 60 * 1000, // 2 minutes
		});

	// Create tag mutation
	const useCreateTag = () =>
		useMutation({
			mutationFn: (data: CreateTagRequest) => tagsApi.createTag(data),
			onSuccess: () => {
				// Invalidate all tag queries to refresh cache
				queryClient.invalidateQueries({ queryKey: tagsQueryKeys.all });
			},
		});

	// Update tag mutation
	const useUpdateTag = () =>
		useMutation({
			mutationFn: ({ tagId, data }: { tagId: string; data: UpdateTagRequest }) =>
				tagsApi.updateTag(tagId, data),
			onSuccess: () => {
				queryClient.invalidateQueries({ queryKey: tagsQueryKeys.all });
			},
		});

	// Merge tags mutation
	const useMergeTags = () =>
		useMutation({
			mutationFn: (data: MergeTagsRequest) => tagsApi.mergeTags(data),
			onSuccess: () => {
				queryClient.invalidateQueries({ queryKey: tagsQueryKeys.all });
			},
		});

	// Delete tag mutation
	const useDeleteTag = () =>
		useMutation({
			mutationFn: (tagId: string) => tagsApi.deleteTag(tagId),
			onSuccess: () => {
				queryClient.invalidateQueries({ queryKey: tagsQueryKeys.all });
			},
		});

	// Attach tag to workflow
	const useAttachTag = () =>
		useMutation({
			mutationFn: ({ workflowId, tagId }: { workflowId: string; tagId: string }) =>
				tagsApi.attachTagToWorkflow(workflowId, tagId),
			onSuccess: (_, { workflowId }) => {
				queryClient.invalidateQueries({ queryKey: tagsQueryKeys.workflow(workflowId) });
				queryClient.invalidateQueries({ queryKey: tagsQueryKeys.all });
			},
		});

	// Detach tag from workflow
	const useDetachTag = () =>
		useMutation({
			mutationFn: ({ workflowId, tagId }: { workflowId: string; tagId: string }) =>
				tagsApi.detachTagFromWorkflow(workflowId, tagId),
			onSuccess: (_, { workflowId }) => {
				queryClient.invalidateQueries({ queryKey: tagsQueryKeys.workflow(workflowId) });
				queryClient.invalidateQueries({ queryKey: tagsQueryKeys.all });
			},
		});

	// Recompute usage counts
	const useRecomputeUsage = () =>
		useMutation({
			mutationFn: () => tagsApi.recomputeUsageCounts(),
			onSuccess: () => {
				queryClient.invalidateQueries({ queryKey: tagsQueryKeys.all });
			},
		});

	// Utility functions
	const invalidateAllTags = useCallback(() => {
		queryClient.invalidateQueries({ queryKey: tagsQueryKeys.all });
	}, [queryClient]);

	const invalidateWorkflowTags = useCallback((workflowId: string) => {
		queryClient.invalidateQueries({ queryKey: tagsQueryKeys.workflow(workflowId) });
	}, [queryClient]);

	// Convert legacy string tags to Tag objects (for backward compatibility)
	const convertLegacyTags = useCallback((stringTags: string[]): Tag[] => {
		return stringTags.map((name, index) => ({
			id: `legacy-${index}`,
			orgId: '',
			name,
			slug: name.toLowerCase().replace(/\s+/g, '-'),
			usageCount: 0,
			createdAt: new Date().toISOString(),
			updatedAt: new Date().toISOString(),
			colorHue: Array.from(name).reduce(
				(acc, char) => (acc * 31 + char.charCodeAt(0)) % 360,
				0
			),
		}));
	}, []);

	return {
		// Query hooks
		useTagSearch,
		useTagSuggestions,
		useTagsList,
		useWorkflowTags,

		// Mutation hooks
		useCreateTag,
		useUpdateTag,
		useMergeTags,
		useDeleteTag,
		useAttachTag,
		useDetachTag,
		useRecomputeUsage,

		// Utility functions
		invalidateAllTags,
		invalidateWorkflowTags,
		convertLegacyTags,
	};
}

/**
 * Simple hook for tag search with debouncing
 */
export function useTagSearch(query: string, enabled = true) {
	const { useTagSearch } = useTags();
	return useTagSearch({ q: query, limit: 20 }, enabled);
}

/**
 * Simple hook for tag suggestions
 */
export function useTagSuggestions(limit = 10) {
	const { useTagSuggestions } = useTags();
	return useTagSuggestions({ limit });
}
